import React from 'react';
import { Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

export interface ContentTitleProps {
  /** Content object — needs a title (or name fallback). */
  content: { title?: string | null; name?: string | null };
  /** Line-clamp count. `undefined` = no clamp. `1` = single-line ellipsis (default). */
  clamp?: number;
  /** Heading variant. Default 'div' (no semantic heading; the wrapper decides). */
  as?: 'div' | 'h2' | 'h3' | 'h4';
  /** Optional sx overrides. */
  sx?: SxProps<Theme>;
}

/**
 * Renders the content's title text. Single-line ellipsis by default; pass
 * `clamp={N}` to allow N lines. No auth, no navigation.
 */
export const ContentTitle: React.FC<ContentTitleProps> = ({ content, clamp = 1, as = 'div', sx }) => {
  const title = content.title || content.name || 'Untitled';
  const clampSx: SxProps<Theme> = clamp
    ? clamp === 1
      ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
      : {
          display: '-webkit-box',
          WebkitLineClamp: clamp,
          WebkitBoxOrient: 'vertical' as const,
          overflow: 'hidden',
        }
    : {};
  return (
    <Typography
      component={as}
      sx={[
        {
          fontSize: 15,
          fontWeight: 700,
          lineHeight: 1.3,
          letterSpacing: '-0.005em',
          color: (theme) => theme.palette.text.primary,
          margin: 0,
        },
        clampSx,
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {title}
    </Typography>
  );
};

export default ContentTitle;
